import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  CheckCircle, Loader2, AlertCircle, GraduationCap, User, Briefcase,
  Star, FileText, Shield
} from "lucide-react";
import PhoneInputWithCode from "@/components/PhoneInputWithCode";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC","PR","GU","VI","AS","MP"
];

const REFERENCE_TYPES = [
  "Instructor", "Occupational Therapy Clinician", "Other Clinician",
  "Physical Therapy Clinician", "Professional (Non-Clinician)",
  "Professional (Other)", "Supervisor"
];

const DURATION_OPTIONS = [
  "Less than 1 year", "1-2 years", "2-5 years", "5-10 years", "More than 10 years"
];

const SUPPORT_OPTIONS = [
  "Strongly Recommend", "Recommend", "Recommend with Reservations", "Do Not Recommend"
];

const RATING_CATEGORIES = [
  { id: "attitude_and_personality", label: "Attitude and Personality", desc: "Positive disposition, ability to work with people, acceptance of criticism" },
  { id: "reliability_and_character", label: "Reliability and Character", desc: "Dependability, willingness, honesty, moral character" },
  { id: "healthy_example", label: "Healthy Example", desc: "Reflects a personal example of a healthy and productive lifestyle" },
  { id: "work_ethic", label: "Work Ethic", desc: "Conscientiousness, follow through, resourcefulness, self-discipline, initiative" },
  { id: "emotional_stability", label: "Emotional Stability", desc: "Appropriate reaction to stress, poise, control, inspires confidence" },
  { id: "independent_thinking", label: "Capacity for Independent Thinking", desc: "Leadership ability, creative thought, curiosity, active learning" },
  { id: "judgment_and_common_sense", label: "Judgment and Common Sense", desc: "Puts foresight into decisions, expresses opinions in mature manner" },
  { id: "communication_skills", label: "Communication Skills", desc: "Ability to communicate with people, including verbally and in writing" },
];

const RATING_OPTIONS = ["Exceptional", "Above Average", "Average", "Below Average", "Unwilling/Unable to Assess"];

export default function RecommenderPortal() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [portalData, setPortalData] = useState(null);

  // Form state
  const [recommenderInfo, setRecommenderInfo] = useState({
    first_name: "", last_name: "", place_of_employment: "", title: "",
    license_number: "", work_email: "", work_phone: "", alternative_phone: "",
    work_city: "", work_state: "", work_zip: ""
  });
  const [affiliation, setAffiliation] = useState({
    type_of_reference: "", how_long_known: "", usa_graduate: ""
  });
  const [ratings, setRatings] = useState({});
  const [details, setDetails] = useState({
    applicants_strengths: "", areas_needing_improvement: "",
    overall_impression: "", overall_support: ""
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchPortalData();
  }, [token]);

  const fetchPortalData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/recommend/${token}`);
      if (!res.ok) throw new Error("Invalid or expired link");
      const data = await res.json();
      if (data.completed) {
        setSubmitted(true);
      } else {
        setPortalData(data);
        const rec = data.recommender || {};
        setRecommenderInfo(prev => ({
          ...prev,
          first_name: rec.first_name || "",
          last_name: rec.last_name || "",
          place_of_employment: rec.place_of_employment || "",
          work_email: rec.work_email || "",
          work_phone: rec.work_phone || "",
          work_city: rec.work_city || "",
          work_state: rec.work_state || "",
          work_zip: rec.work_zip || "",
        }));
        if (rec.recommendation_type) {
          setAffiliation(prev => ({ ...prev, type_of_reference: rec.recommendation_type }));
        }
      }
    } catch (err) {
      setError(err.message || "Failed to load recommendation form");
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const e = {};
    if (!recommenderInfo.first_name.trim()) e.ri_first_name = true;
    if (!recommenderInfo.last_name.trim()) e.ri_last_name = true;
    if (!recommenderInfo.place_of_employment.trim()) e.ri_employment = true;
    if (!recommenderInfo.title.trim()) e.ri_title = true;
    if (!recommenderInfo.work_email.trim()) e.ri_email = true;
    if (!recommenderInfo.work_phone.trim()) e.ri_phone = true;
    if (!recommenderInfo.work_city.trim()) e.ri_city = true;
    if (!recommenderInfo.work_state) e.ri_state = true;
    if (!recommenderInfo.work_zip.trim()) e.ri_zip = true;
    if (!affiliation.type_of_reference) e.af_type = true;
    if (!affiliation.how_long_known) e.af_duration = true;
    if (!affiliation.usa_graduate) e.af_graduate = true;
    RATING_CATEGORIES.forEach(cat => {
      if (!ratings[cat.id]) e[`rating_${cat.id}`] = true;
    });
    if (!details.applicants_strengths.trim()) e.d_strengths = true;
    if (!details.areas_needing_improvement.trim()) e.d_improvement = true;
    if (!details.overall_impression.trim()) e.d_impression = true;
    if (!details.overall_support) e.d_support = true;
    setFormErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/recommend/${token}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          recommender_info: recommenderInfo,
          affiliation,
          overall_rating: ratings,
          details,
        }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || "Submission failed");
      }
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = (errKey) =>
    `h-11 bg-black/30 border-white/[0.08] text-white rounded-xl px-4 focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 placeholder:text-slate-600 ${formErrors[errKey] ? "border-red-400/50 ring-1 ring-red-400/30" : ""}`;
  const labelCls = "text-sm text-slate-300 flex items-center gap-1.5 mb-1.5";
  const sectionCard = "backdrop-blur-xl bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6 md:p-8 space-y-6 hover:border-white/[0.08] transition-colors duration-500";

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E14] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#00677F] animate-spin" />
      </div>
    );
  }

  if (error && !portalData) {
    return (
      <div className="min-h-screen bg-[#0A0E14] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">Link Invalid</h1>
          <p className="text-slate-400">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0A0E14] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center animate-in fade-in zoom-in-95 duration-500">
          <div className="w-20 h-20 rounded-full bg-[#739600]/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-[#739600]" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">Thank You!</h1>
          <p className="text-slate-400 mb-2">Your recommendation has been submitted successfully.</p>
          <p className="text-slate-500 text-sm">
            The applicant and the admissions team have been notified. You may close this window.
          </p>
        </div>
      </div>
    );
  }

  const hasErrors = Object.keys(formErrors).length > 0;

  return (
    <div className="min-h-screen bg-[#0A0E14]">
      {/* Header */}
      <div className="border-b border-white/[0.06]">
        <div className="max-w-4xl mx-auto px-4 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#00677F]/20 flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-[#A1D8E0]" />
            </div>
            <div>
              <h1 className="text-white font-semibold text-sm">University of St. Augustine</h1>
              <p className="text-slate-500 text-xs">for Health Sciences</p>
            </div>
          </div>
          <span className="text-slate-600 text-xs uppercase tracking-wider">Letter of Recommendation</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">

        {/* Error banner */}
        {hasErrors && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4 flex items-center gap-3" data-testid="portal-error-banner">
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
            <p className="text-red-400 text-sm">Please fill in all required fields marked with <span className="text-red-400 font-bold">*</span> before submitting.</p>
          </div>
        )}

        {/* Intro card */}
        <div className={sectionCard}>
          <div className="space-y-4">
            <p className="text-slate-300 text-sm leading-relaxed">
              The person listed below is applying for admission to the University of St. Augustine for Health Sciences. 
              In order to be considered for admission, applicants must provide letters of recommendation supporting their candidacy. 
              These letters generally speak to an <strong className="text-white">applicant's ability to excel in a rigorous educational endeavor 
              and/or an ability to thrive as a working, licensed therapist</strong>. You have been identified as someone who can offer a reference supporting this applicant.
            </p>
            <p className="text-slate-400 text-sm leading-relaxed">
              If you are willing, please complete this short electronic reference form, <strong className="text-slate-200">rendering your impressions of the applicant's 
              qualifications, work ethic, education and other factors</strong> you have witnessed or experienced.
            </p>
            <p className="text-slate-500 text-xs">
              Your prompt attention to this matter is a critical piece in the student's application process.
            </p>
          </div>
        </div>

        {/* 1. Applicant Information */}
        <div className={sectionCard} data-testid="portal-applicant-info">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-xl bg-[#00677F]/20 flex items-center justify-center">
              <User className="w-4 h-4 text-[#A1D8E0]" />
            </div>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-[#A1D8E0]">Applicant Information</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="p-4 rounded-xl bg-black/20 border border-white/[0.05]">
              <p className="text-slate-500 text-xs uppercase tracking-wider mb-1">Person requesting this letter</p>
              <p className="text-white font-medium">{portalData?.applicant_name || "—"}</p>
            </div>
            <div className="p-4 rounded-xl bg-black/20 border border-white/[0.05]">
              <p className="text-slate-500 text-xs uppercase tracking-wider mb-1">Applying to</p>
              <p className="text-white font-medium">{portalData?.program || "—"}</p>
            </div>
          </div>
        </div>

        {/* 2. Recommender Information */}
        <div className={sectionCard} data-testid="portal-recommender-info">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-xl bg-[#7B68EE]/20 flex items-center justify-center">
              <Briefcase className="w-4 h-4 text-[#7B68EE]" />
            </div>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-[#7B68EE]">Recommender Information</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
            <div>
              <Label className={labelCls}><span className="text-red-400">*</span> First Name</Label>
              <Input value={recommenderInfo.first_name} onChange={e => setRecommenderInfo(p => ({ ...p, first_name: e.target.value }))}
                placeholder="First Name" className={inputCls("ri_first_name")} data-testid="portal-first-name" />
            </div>
            <div>
              <Label className={labelCls}><span className="text-red-400">*</span> Last Name</Label>
              <Input value={recommenderInfo.last_name} onChange={e => setRecommenderInfo(p => ({ ...p, last_name: e.target.value }))}
                placeholder="Last Name" className={inputCls("ri_last_name")} data-testid="portal-last-name" />
            </div>
            <div>
              <Label className={labelCls}><span className="text-red-400">*</span> Place of Employment</Label>
              <Input value={recommenderInfo.place_of_employment} onChange={e => setRecommenderInfo(p => ({ ...p, place_of_employment: e.target.value }))}
                placeholder="Organization" className={inputCls("ri_employment")} data-testid="portal-employment" />
            </div>
            <div>
              <Label className={labelCls}><span className="text-red-400">*</span> Title</Label>
              <Input value={recommenderInfo.title} onChange={e => setRecommenderInfo(p => ({ ...p, title: e.target.value }))}
                placeholder="Job Title" className={inputCls("ri_title")} data-testid="portal-title" />
            </div>
            <div>
              <Label className={labelCls}>License Number</Label>
              <Input value={recommenderInfo.license_number} onChange={e => setRecommenderInfo(p => ({ ...p, license_number: e.target.value }))}
                placeholder="Optional" className={inputCls("")} data-testid="portal-license" />
            </div>
            <div>
              <Label className={labelCls}><span className="text-red-400">*</span> Work Email</Label>
              <Input type="email" value={recommenderInfo.work_email} onChange={e => setRecommenderInfo(p => ({ ...p, work_email: e.target.value }))}
                placeholder="email@work.com" className={inputCls("ri_email")} data-testid="portal-email" />
            </div>
            <div>
              <Label className={labelCls}><span className="text-red-400">*</span> Work Phone</Label>
              <PhoneInputWithCode
                value={recommenderInfo.work_phone}
                countryCode={recommenderInfo.work_phone_country_code || "+1|US"}
                onPhoneChange={(val) => setRecommenderInfo(p => ({ ...p, work_phone: val }))}
                onCodeChange={(val) => setRecommenderInfo(p => ({ ...p, work_phone_country_code: val }))}
                testId="portal-phone"
                theme="dark"
              />
            </div>
            <div>
              <Label className={labelCls}>Alternative Phone</Label>
              <PhoneInputWithCode
                value={recommenderInfo.alternative_phone}
                countryCode={recommenderInfo.alt_phone_country_code || "+1|US"}
                onPhoneChange={(val) => setRecommenderInfo(p => ({ ...p, alternative_phone: val }))}
                onCodeChange={(val) => setRecommenderInfo(p => ({ ...p, alt_phone_country_code: val }))}
                testId="portal-alt-phone"
                theme="dark"
              />
            </div>
            <div>
              <Label className={labelCls}><span className="text-red-400">*</span> Work City</Label>
              <Input value={recommenderInfo.work_city} onChange={e => setRecommenderInfo(p => ({ ...p, work_city: e.target.value }))}
                placeholder="City" className={inputCls("ri_city")} data-testid="portal-city" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={labelCls}><span className="text-red-400">*</span> State</Label>
                <Select value={recommenderInfo.work_state} onValueChange={v => setRecommenderInfo(p => ({ ...p, work_state: v }))}>
                  <SelectTrigger className={`h-11 bg-black/30 border-white/[0.08] text-white rounded-xl ${formErrors.ri_state ? "border-red-400/50" : ""}`} data-testid="portal-state">
                    <SelectValue placeholder="State" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f1419] border-white/10 max-h-[200px]">
                    {US_STATES.map(s => <SelectItem key={s} value={s} className="text-slate-300 hover:bg-white/5">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={labelCls}><span className="text-red-400">*</span> Zip</Label>
                <Input value={recommenderInfo.work_zip} onChange={e => setRecommenderInfo(p => ({ ...p, work_zip: e.target.value }))}
                  placeholder="00000" className={inputCls("ri_zip")} data-testid="portal-zip" />
              </div>
            </div>
          </div>
        </div>

        {/* 3. Affiliation */}
        <div className={sectionCard} data-testid="portal-affiliation">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-xl bg-[#FF9800]/20 flex items-center justify-center">
              <Briefcase className="w-4 h-4 text-[#FF9800]" />
            </div>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-[#FF9800]">Affiliation</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
            <div>
              <Label className={labelCls}><span className="text-red-400">*</span> Type of Reference</Label>
              <Select value={affiliation.type_of_reference} onValueChange={v => setAffiliation(p => ({ ...p, type_of_reference: v }))}>
                <SelectTrigger className={`h-11 bg-black/30 border-white/[0.08] text-white rounded-xl ${formErrors.af_type ? "border-red-400/50" : ""}`} data-testid="portal-ref-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-[#0f1419] border-white/10">
                  {REFERENCE_TYPES.map(t => <SelectItem key={t} value={t} className="text-slate-300 hover:bg-white/5">{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={labelCls}><span className="text-red-400">*</span> How Long Have You Known the Applicant?</Label>
              <Select value={affiliation.how_long_known} onValueChange={v => setAffiliation(p => ({ ...p, how_long_known: v }))}>
                <SelectTrigger className={`h-11 bg-black/30 border-white/[0.08] text-white rounded-xl ${formErrors.af_duration ? "border-red-400/50" : ""}`} data-testid="portal-duration">
                  <SelectValue placeholder="Select duration" />
                </SelectTrigger>
                <SelectContent className="bg-[#0f1419] border-white/10">
                  {DURATION_OPTIONS.map(d => <SelectItem key={d} value={d} className="text-slate-300 hover:bg-white/5">{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4">
            <Label className={labelCls}><span className="text-red-400">*</span> Did You Graduate from the University of St. Augustine?</Label>
            <RadioGroup value={affiliation.usa_graduate} onValueChange={v => setAffiliation(p => ({ ...p, usa_graduate: v }))} className="flex gap-4 mt-2">
              {[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }].map(opt => (
                <div key={opt.value} className={`flex items-center space-x-2 px-6 py-3 rounded-full border transition-all duration-300 cursor-pointer ${
                  affiliation.usa_graduate === opt.value
                    ? "border-[#00677F]/50 bg-[#00677F]/10"
                    : `border-white/[0.08] bg-black/30 hover:border-white/20 ${formErrors.af_graduate ? "border-red-400/30" : ""}`
                }`}>
                  <RadioGroupItem value={opt.value} id={`usa-grad-${opt.value}`} />
                  <Label htmlFor={`usa-grad-${opt.value}`} className="text-white cursor-pointer">{opt.label}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        </div>

        {/* 4. Overall Rating */}
        <div className={sectionCard} data-testid="portal-overall-rating">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-xl bg-[#E91E63]/20 flex items-center justify-center">
              <Star className="w-4 h-4 text-[#E91E63]" />
            </div>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-[#E91E63]">Overall Rating</h2>
          </div>
          <div className="space-y-6 mt-4">
            {RATING_CATEGORIES.map(cat => (
              <div key={cat.id} className={`p-5 rounded-xl border transition-all duration-300 ${
                formErrors[`rating_${cat.id}`]
                  ? "border-red-400/30 bg-red-500/5"
                  : ratings[cat.id]
                    ? "border-[#739600]/20 bg-[#739600]/5"
                    : "border-white/[0.05] bg-black/20"
              }`}>
                <div className="mb-3">
                  <p className="text-white font-medium text-sm flex items-center gap-1.5">
                    {cat.label} <span className="text-red-400">*</span>
                  </p>
                  <p className="text-slate-500 text-xs mt-0.5">{cat.desc}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {RATING_OPTIONS.map(opt => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setRatings(p => ({ ...p, [cat.id]: opt }))}
                      className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 ${
                        ratings[cat.id] === opt
                          ? opt === "Exceptional" ? "bg-[#739600] text-white shadow-[0_0_12px_rgba(115,150,0,0.3)]"
                            : opt === "Above Average" ? "bg-[#00677F] text-white shadow-[0_0_12px_rgba(0,103,127,0.3)]"
                            : opt === "Average" ? "bg-[#FF9800] text-white"
                            : opt === "Below Average" ? "bg-[#FF5252] text-white"
                            : "bg-slate-600 text-white"
                          : "bg-white/5 text-slate-400 border border-white/[0.08] hover:border-white/20 hover:bg-white/10"
                      }`}
                      data-testid={`rating-${cat.id}-${opt.replace(/[\s\/]/g, '-').toLowerCase()}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 5. Details and Confirmation */}
        <div className={sectionCard} data-testid="portal-details">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-xl bg-[#9C27B0]/20 flex items-center justify-center">
              <FileText className="w-4 h-4 text-[#9C27B0]" />
            </div>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-[#9C27B0]">Details and Confirmation</h2>
          </div>
          <div className="space-y-5 mt-4">
            <div>
              <Label className={labelCls}><span className="text-red-400">*</span> Applicant's Strengths</Label>
              <Textarea value={details.applicants_strengths} onChange={e => setDetails(p => ({ ...p, applicants_strengths: e.target.value }))}
                placeholder="Describe the applicant's strengths..."
                className={`min-h-[120px] bg-black/30 border-white/[0.08] text-white rounded-2xl resize-none placeholder:text-slate-600 ${formErrors.d_strengths ? "border-red-400/50" : ""}`}
                data-testid="portal-strengths" />
            </div>
            <div>
              <Label className={labelCls}><span className="text-red-400">*</span> Areas Needing Improvement</Label>
              <Textarea value={details.areas_needing_improvement} onChange={e => setDetails(p => ({ ...p, areas_needing_improvement: e.target.value }))}
                placeholder="Describe areas where the applicant could improve..."
                className={`min-h-[120px] bg-black/30 border-white/[0.08] text-white rounded-2xl resize-none placeholder:text-slate-600 ${formErrors.d_improvement ? "border-red-400/50" : ""}`}
                data-testid="portal-improvement" />
            </div>
            <div>
              <Label className={labelCls}><span className="text-red-400">*</span> Your Overall Impression</Label>
              <Textarea value={details.overall_impression} onChange={e => setDetails(p => ({ ...p, overall_impression: e.target.value }))}
                placeholder="Share your overall impression of the applicant..."
                className={`min-h-[120px] bg-black/30 border-white/[0.08] text-white rounded-2xl resize-none placeholder:text-slate-600 ${formErrors.d_impression ? "border-red-400/50" : ""}`}
                data-testid="portal-impression" />
            </div>
            <div>
              <Label className={labelCls}><span className="text-red-400">*</span> My Overall Support for this Applicant</Label>
              <Select value={details.overall_support} onValueChange={v => setDetails(p => ({ ...p, overall_support: v }))}>
                <SelectTrigger className={`h-11 bg-black/30 border-white/[0.08] text-white rounded-xl ${formErrors.d_support ? "border-red-400/50" : ""}`} data-testid="portal-support">
                  <SelectValue placeholder="Select level of support" />
                </SelectTrigger>
                <SelectContent className="bg-[#0f1419] border-white/10">
                  {SUPPORT_OPTIONS.map(s => <SelectItem key={s} value={s} className="text-slate-300 hover:bg-white/5">{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* FERPA Notice */}
        <div className="backdrop-blur-xl bg-[#FF9800]/5 border border-[#FF9800]/15 rounded-2xl p-6" data-testid="portal-ferpa">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-[#FF9800]/10 flex items-center justify-center shrink-0 mt-0.5">
              <Shield className="w-5 h-5 text-[#FF9800]" />
            </div>
            <div className="space-y-2">
              <p className="text-slate-300 text-sm leading-relaxed">
                <strong className="text-[#FF9800]">Note:</strong> Under the provisions of the Family Educational Rights and Privacy Act of 1974, 
                only registered (admitted) students and alumni have access to their educational records — not simply "applicants" for admission. 
                The information you are providing will be used solely for determining whether an applicant is admitted to the University, 
                and it will be removed from a student's file and destroyed if/when the student has matriculated.
              </p>
              <p className="text-slate-400 text-sm font-medium mt-3">
                By clicking <strong className="text-white">Submit</strong>, I acknowledge that the information provided is accurate and truthful.
              </p>
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end gap-4 pb-12">
          <Button
            variant="outline"
            className="border-white/20 text-white hover:bg-white/5 rounded-xl px-8 h-12"
            onClick={() => window.close()}
            data-testid="portal-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-[#00677F] hover:bg-[#135163] text-white rounded-xl px-8 h-12 shadow-[0_0_20px_rgba(0,103,127,0.3)] hover:shadow-[0_0_30px_rgba(0,103,127,0.5)] transition-all"
            data-testid="portal-submit-btn"
          >
            {submitting ? (
              <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</span>
            ) : (
              "Submit Recommendation"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
